import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  setDoc,
  where,
  arrayUnion,
} from "@firebase/firestore";
import { auth, db } from "./firebase";
import { EXERCISE_DEFAULTS } from "../data/exercise-defaults";

/**
 * The exercise library (knjižnica vježbi): the built-in exercises from
 * public/exercises.json plus the `exercises` collection in Firestore.
 *
 * A Firestore document with the same id as a built-in exercise replaces it,
 * which is how built-in exercises are edited, and `deleted: true` on such a
 * document hides it (deleting the document would bring the built-in one
 * back). Plans copy an exercise when it's added, so screens that show plans
 * look the exercise up here again (lib/exercise-library.ts) to show the
 * library's current name, video and description.
 */
export type LibraryExercise = {
  id: string;
  title: string;
  description?: string;
  videoUrl?: string;
  createdBy?: string; // To identify custom exercises
  bodyTag?: string; // Optional because old custom exercises might not have it yet
  // Earlier names, so plans that still use them find the exercise.
  aliases?: string[];
  builtIn?: boolean;
};

type StoredExercise = LibraryExercise & { deleted?: boolean };

const EXERCISES_COLLECTION = "exercises";

// Cache for static exercises to avoid repeated fetches
let staticExercisesCache: LibraryExercise[] | null = null;
let staticExercisesPromise: Promise<LibraryExercise[]> | null = null;

export async function loadStaticExercises(): Promise<LibraryExercise[]> {
  // Return cached result if available
  if (staticExercisesCache) {
    return staticExercisesCache;
  }

  // Return existing promise if load is in progress
  if (staticExercisesPromise) {
    return staticExercisesPromise;
  }

  // Start loading
  staticExercisesPromise = (async () => {
    try {
      // Fetch from public folder (we copied exercises.json there)
      const response = await fetch("/exercises.json");
      if (!response.ok) {
        throw new Error(
          `Failed to fetch exercises.json: ${response.statusText}`
        );
      }
      const exercisesData = await response.json();
      const exercises = exercisesData.map((ex: any) => ({
        id: ex.id,
        title: ex.title,
        description: ex.description,
        videoUrl: ex.videoUrl,
        bodyTag: ex.bodyTag,
        builtIn: true,
      })) as LibraryExercise[];

      // Cache the result
      staticExercisesCache = exercises;
      return exercises;
    } catch (error) {
      console.error("Error loading exercises.json:", error);
      // Reset cache on error so it tries again next time
      staticExercisesCache = null;
      staticExercisesPromise = null;
      return [];
    }
  })();

  return staticExercisesPromise;
}

/**
 * Fills in the default description and body part where the library has none
 * (a description that is only a link counts as none).
 */
function withDefaults(ex: LibraryExercise): LibraryExercise {
  const defaults = EXERCISE_DEFAULTS[ex.id];
  if (!defaults) return ex;
  const own = ex.description?.trim();
  return {
    ...ex,
    description: own && !/^https?:\/\/\S+$/.test(own) ? own : defaults.description,
    bodyTag: ex.bodyTag || defaults.bodyTag,
  };
}

async function getFirestoreExercises(): Promise<StoredExercise[]> {
  const q = query(collection(db, EXERCISES_COLLECTION), orderBy("title", "asc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => {
    const data = d.data();
    // Use the 'id' field from the document data, not the Firestore document ID
    // This ensures consistency with how exercises are created (id field = doc.id)
    return {
      id: data.id || d.id, // Fallback to doc.id if id field is missing
      title: data.title,
      description: data.description,
      videoUrl: data.videoUrl,
      createdBy: data.createdBy,
      bodyTag: data.bodyTag,
      aliases: Array.isArray(data.aliases) ? data.aliases : undefined,
      deleted: data.deleted === true,
    };
  });
}

/**
 * The whole library. `complete` is false when the Firestore part couldn't be
 * read (offline), so only the built-in exercises are in it.
 */
export async function loadExerciseLibrary(): Promise<{ exercises: LibraryExercise[]; complete: boolean }> {
  // Load exercises from exercises.json (static file)
  const staticExercises = await loadStaticExercises();

  // Load custom exercises from Firestore
  let firestoreExercises: StoredExercise[] = [];
  let complete = true;
  try {
    firestoreExercises = await getFirestoreExercises();
  } catch (firestoreError) {
    complete = false;
    console.warn(
      "Error reading Firestore exercises (continuing with static exercises):",
      firestoreError
    );
  }

  // Merge static and Firestore exercises, with Firestore taking precedence for duplicates (by id)
  const exerciseMap = new Map<string, StoredExercise>();
  staticExercises.forEach((ex) => exerciseMap.set(ex.id, ex));
  firestoreExercises.forEach((ex) => {
    const builtIn = exerciseMap.get(ex.id)?.builtIn;
    exerciseMap.set(ex.id, builtIn ? { ...ex, builtIn } : ex);
  });

  const exercises = Array.from(exerciseMap.values())
    .filter((ex) => !ex.deleted && typeof ex.title === "string")
    .map((ex) => {
      const exercise: LibraryExercise = { ...ex };
      delete (exercise as StoredExercise).deleted;
      return withDefaults(exercise);
    })
    .sort((a, b) => a.title.localeCompare(b.title));
  return { exercises, complete };
}

export async function getExercisesFromFileDb(): Promise<LibraryExercise[]> {
  try {
    return (await loadExerciseLibrary()).exercises;
  } catch (error) {
    console.error("Error reading exercise database:", error);
    // Return empty array if there's an error
    return [];
  }
}

export async function addExerciseToFileDb(exerciseData: {
  title: string;
  description?: string;
  videoUrl?: string;
  createdBy: string;
  bodyTag?: string;
}): Promise<LibraryExercise> {
  const exerciseRef = doc(collection(db, EXERCISES_COLLECTION));

  const newExercise: LibraryExercise = {
    id: exerciseRef.id,
    title: exerciseData.title,
    description: exerciseData.description,
    videoUrl: exerciseData.videoUrl,
    createdBy: exerciseData.createdBy,
    bodyTag: exerciseData.bodyTag,
  };

  await setDoc(exerciseRef, newExercise);
  return newExercise;
}

/** The Firestore documents for an exercise id (normally one; none for an unedited built-in exercise). */
async function findExerciseDocs(exerciseId: string) {
  const snap = await getDocs(
    query(collection(db, EXERCISES_COLLECTION), where("id", "==", exerciseId))
  );
  return snap.docs;
}

async function findBuiltIn(exerciseId: string): Promise<LibraryExercise | undefined> {
  return (await loadStaticExercises()).find((ex) => ex.id === exerciseId);
}

/** A Firestore copy of a built-in exercise, which then replaces it in the library. */
async function createBuiltInOverride(builtIn: LibraryExercise, data: Record<string, unknown>) {
  const override = Object.fromEntries(
    Object.entries({ ...builtIn, ...data, id: builtIn.id, createdBy: auth.currentUser?.uid })
      .filter(([key, value]) => value !== undefined && key !== "builtIn")
  );
  await setDoc(doc(db, EXERCISES_COLLECTION, builtIn.id), override);
}

export async function updateExerciseInFileDb(
  exerciseId: string,
  data: Partial<Omit<LibraryExercise, "id" | "createdBy" | "aliases" | "builtIn">>
): Promise<void> {
  const docs = await findExerciseDocs(exerciseId);

  if (docs.length === 0) {
    const builtIn = await findBuiltIn(exerciseId);
    if (!builtIn) throw new Error(`Exercise with id ${exerciseId} not found`);
    const renamed = data.title !== undefined && data.title.trim() !== builtIn.title.trim();
    await createBuiltInOverride(builtIn, {
      ...data,
      ...(renamed ? { aliases: [builtIn.title] } : {}),
    });
    return;
  }

  if (docs.length > 1) {
    console.warn(
      `Multiple exercises found with id ${exerciseId}, updating first one`
    );
  }
  const current = docs[0].data();
  // Keep the old name: plans that still use it then find the exercise.
  const renamed =
    data.title !== undefined &&
    typeof current.title === "string" &&
    data.title.trim() !== current.title.trim();
  await updateDoc(docs[0].ref, {
    ...data,
    ...(renamed ? { aliases: arrayUnion(current.title) } : {}),
  });
}

export async function deleteExerciseFromFileDb(
  exerciseId: string
): Promise<void> {
  const docs = await findExerciseDocs(exerciseId);
  const builtIn = await findBuiltIn(exerciseId);

  if (builtIn) {
    // Hidden, not removed: without the document the built-in exercise
    // would come back.
    if (docs.length === 0) await createBuiltInOverride(builtIn, { deleted: true });
    else await Promise.all(docs.map((d) => updateDoc(d.ref, { deleted: true })));
    return;
  }

  if (docs.length === 0) {
    throw new Error(`Exercise with id ${exerciseId} not found`);
  }
  // Delete all matching documents (should only be one, but handle edge case)
  await Promise.all(docs.map((d) => deleteDoc(d.ref)));
}
